import { useState, type FormEvent } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '../../components/ui/dialog.tsx';
import { Button } from '../../components/ui/button.tsx';
import { Input } from '../../components/ui/input.tsx';
import { Label } from '../../components/ui/label.tsx';
import { useCreateTheory } from '../../lib/hooks.ts';
import { ApiError } from '../../lib/api.ts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
}

export function CreateTheoryDialog({ open, onOpenChange, productId }: Props): JSX.Element {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const create = useCreateTheory();

  function reset(): void {
    setSlug('');
    setName('');
    setError(null);
  }

  function handleClose(next: boolean): void {
    if (!next) reset();
    onOpenChange(next);
  }

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    try {
      await create.mutateAsync({ product_id: productId, slug: slug.trim(), name: name.trim() });
      reset();
      onOpenChange(false);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'slug_conflict') {
        setError('Идентификатор уже занят в этом продукте.');
      } else if (err instanceof ApiError && err.status === 422) {
        setError('Идентификатор должен быть из латинских букв, цифр и дефисов.');
      } else {
        setError(err instanceof Error ? err.message : 'Не удалось создать теорию');
      }
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <form onSubmit={onSubmit}>
          <DialogTitle>Новая теория</DialogTitle>
          <DialogDescription>
            Каждая теория собирает свою воронку независимо от других.
          </DialogDescription>

          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="theory-name">Название</Label>
              <Input
                id="theory-name"
                placeholder="Например: Весенний запуск"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                maxLength={200}
                autoFocus
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="theory-slug">Идентификатор</Label>
              <Input
                id="theory-slug"
                placeholder="spring-launch"
                value={slug}
                onChange={(e) => setSlug(e.target.value.toLowerCase())}
                required
                maxLength={100}
                pattern="^[a-z0-9](?:[a-z0-9-]{0,98}[a-z0-9])?$"
                className="font-mono"
              />
              <p className="text-xs text-fg-muted">
                Только латиница, цифры и дефис. Заглавные приводятся к нижнему регистру
                автоматически.
              </p>
            </div>
          </div>

          {error ? (
            <p className="mt-3 rounded border border-danger/40 bg-danger/10 p-2 text-sm text-danger">
              {error}
            </p>
          ) : null}

          <div className="mt-6 flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={() => handleClose(false)}>
              Отмена
            </Button>
            <Button type="submit" variant="primary" disabled={create.isPending}>
              {create.isPending ? 'Создаём…' : 'Создать'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
